import { domToPng } from 'modern-screenshot';

// About an email client's reading pane: the 603px email card with margins,
// whatever size the admin's window is.
export const EMAIL_WIDTH = 780;
export const PIXEL_RATIO = 2;
// A stalled image request would otherwise keep the button busy until the
// browser's own network timeout.
export const CAPTURE_TIMEOUT_MS = 30000;

// An image that fails to load (a missing logo, a host without CORS headers)
// draws as this 1x1 transparent GIF instead.
export const BLANK_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

export const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Preview capture timed out after ${ms}ms`)),
      ms,
    );
    promise.then(resolve, reject).finally(() => clearTimeout(timer));
  });

export const saveDataUrl = (dataUrl, fileName) => {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = fileName;
  link.click();
};

const imagesLoaded = doc =>
  Promise.all(
    [...doc.images].map(image =>
      image.complete
        ? null
        : new Promise(resolve => {
            image.onload = resolve;
            image.onerror = resolve;
          }),
    ),
  );

// modern-screenshot only blanks images it can't fetch. One that answers with
// something else (an S3 403's XML, an HTML page) would draw as a broken-image
// icon, so this blanks those too.
export const fetchImage = async url => {
  try {
    // The preview has usually shown this image already, and S3 caches that
    // copy for years without a CORS header, which makes this fetch fail.
    const response = await fetch(url, { cache: 'no-store' });
    const blob = await response.blob();
    if (!response.ok || /^(text\/|application\/(json|xml))/.test(blob.type)) {
      return BLANK_IMAGE;
    }
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return BLANK_IMAGE;
  }
};

// The copy isn't sandboxed: in a frame sandboxed without scripts, WebKit
// never fires the image load events the capture waits on. This policy keeps
// the email inert instead. Nothing in it runs or loads a frame, plugin, form
// or base URL; images, styles and fonts still load.
const COPY_POLICY =
  "default-src 'none'; img-src * data: blob:; style-src * 'unsafe-inline'; font-src * data:; form-action 'none'; base-uri 'none'";

// Without its doctype the copy would render in quirks mode, unlike the
// preview and real inboxes.
const serialize = emailDocument => {
  const doctype = emailDocument.doctype
    ? new XMLSerializer().serializeToString(emailDocument.doctype)
    : '';
  const root = emailDocument.documentElement.cloneNode(true);
  // A refresh still navigates the frame, whatever the policy says.
  root
    .querySelectorAll('meta[http-equiv="refresh" i]')
    .forEach(node => node.remove());
  const policy = emailDocument.createElement('meta');
  policy.setAttribute('http-equiv', 'Content-Security-Policy');
  policy.setAttribute('content', COPY_POLICY);
  // First in <head>, so it applies to everything parsed after it.
  root.querySelector('head').prepend(policy);

  return doctype + root.outerHTML;
};

// Subject large, sender underneath, the way an inbox shows a message.
const addInboxHeader = (doc, { subject, from }) => {
  const line = (text, style) => {
    const element = doc.createElement('div');
    element.setAttribute('style', `overflow-wrap:anywhere;${style}`);
    element.textContent = text;
    return element;
  };

  const header = doc.createElement('div');
  header.setAttribute(
    'style',
    'padding:24px 32px;background:#ffffff;border-bottom:1px solid #e5e5e5;font-family:Helvetica,Arial,sans-serif;',
  );
  header.append(
    line(subject, 'font-size:22px;color:#202124;margin-bottom:10px;'),
    line(from, 'font-size:14px;color:#5f6368;'),
  );
  doc.body.prepend(header);
};

const capture = async (frame, ready, emailDocument, header) => {
  // Firefox replaces a new frame's first document when it loads, discarding
  // anything written before that.
  await ready;
  const doc = frame.contentDocument;
  doc.open();
  doc.write(serialize(emailDocument));
  doc.close();

  doc.querySelectorAll('[data-export-hide]').forEach(node => node.remove());
  addInboxHeader(doc, header);
  await imagesLoaded(doc);

  const height = doc.documentElement.scrollHeight;
  frame.style.height = `${height}px`;

  return domToPng(doc.body, {
    width: EMAIL_WIDTH,
    height,
    scale: PIXEL_RATIO,
    backgroundColor: '#ffffff',
    fetch: { placeholderImage: BLANK_IMAGE },
    fetchFn: fetchImage,
  });
};

// Copies the previewed email into an offscreen frame at EMAIL_WIDTH and
// downloads it as a PNG. The copy is taken from the live document, so edits
// the editor has written into the preview (not yet saved) are included.
// Elements marked data-export-hide, such as the footer's "Do not forward
// this email" login warning, are left out of the copy.
export const downloadEmailPng = async ({
  emailDocument,
  subject,
  from,
  fileName,
}) => {
  // Same origin so the copy can be read and captured; COPY_POLICY keeps the
  // email inert in it.
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = `position:fixed;left:-10000px;top:0;width:${EMAIL_WIDTH}px;height:100px;border:0;`;
  // Listen before inserting: Chrome fires this load during appendChild.
  const ready = new Promise(resolve =>
    frame.addEventListener('load', resolve, { once: true }),
  );
  document.body.appendChild(frame);

  try {
    const png = await withTimeout(
      capture(frame, ready, emailDocument, { subject, from }),
      CAPTURE_TIMEOUT_MS,
    );

    saveDataUrl(png, fileName);
  } finally {
    frame.remove();
  }
};
